import React from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  Typography,
  Paper,
  Stack,
  Avatar,
  IconButton,
  Tooltip,
} from "@mui/material";
import DOMPurify from "dompurify";
import { useNavigate, useParams } from "react-router-dom";
import {
  FaMapMarkerAlt,
  FaBriefcase,
  FaClock,
  FaMoneyBillWave,
  FaCalendarAlt,
  FaArrowLeft,
  FaBookmark,
  FaRegBookmark,
  FaCheckCircle,
  FaHistory,
  FaFileSignature,
  FaLaptopCode,
  FaUserGraduate,
  FaQuestionCircle,
  FaShareAlt,
} from "react-icons/fa";
import Navbar from "../Navbar/Navbar";
import Footer from "../Footer/Footer";
import ShareButtons from "./ShareButtons";
import { useEffect, useState } from "react";
import axios from "axios";
import { Work } from "@mui/icons-material";
import Loader from "../../../Common/Loader/loader";

const BRAND = "#2d6d96";
const GRADIENT = "linear-gradient(135deg, #2d6d96, #4fa3d1)";
const GRADIENT_HOVER = "linear-gradient(135deg, #245777, #3e90bd)";

const JobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [jobDetails, setJobDetails] = useState(null);
  const [isSaved, setIsSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const isLoggedIn = Boolean(localStorage.getItem("token"));

  const handleApply = () => {
    if (!isLoggedIn) {
      navigate("/login", {
        state: { redirectTo: `/ebench/job-details/${id}` },
      });
      return;
    }
    navigate(`/new_job`);
  };

  const fetchJobDetails = async (jobId) => {
    try {
      setLoading(true);
      const response = await axios.get(
        `${process.env.REACT_APP_API_PATH}/ebench/get_job_by_jobId?jobId=${jobId}`
      );
      //   console.log("Job Details Response:", response.data.data);
      setJobDetails(response.data.data);
    } catch (error) {
      console.error("Error fetching job details:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobDetails(id);
  }, [id]);

  if (loading) {
    return (
      <>
        <Navbar />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="80vh"
        >
          <Loader />
          <Typography
            sx={{ marginLeft: 4 }}
            variant="h5"
            color="text.secondary"
          >
            Loading job details...
          </Typography>
        </Box>
        <Footer />
      </>
    );
  }

  const safe = (v) =>
    v === null ||
    v === undefined ||
    v === "" ||
    v === "null" ||
    v === "undefined"
      ? "N/A"
      : v;

  const location = [jobDetails?.city, jobDetails?.state, jobDetails?.country]
    .map(safe)
    .filter((v) => v !== "N/A");

  const formatExperience = (minExp, maxExp) => {
    const extractNumber = (val) => {
      if (!val) return null;
      const num = val.match(/\d+/);
      return num ? Number(num[0]) : null;
    };

    const min = extractNumber(minExp);
    const max = extractNumber(maxExp);

    if (min != null && max != null && max > 0) {
      return `${min} - ${max} Years`;
    }

    if (min != null && min > 0) {
      return `${min}+ Years`;
    }

    return "Experience Not Disclosed";
  };

  if (!jobDetails) {
    return (
      <>
        <Navbar />
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="80vh"
          flexDirection="column"
        >
          <Typography variant="h5" color="text.secondary" mb={2}>
            Job not found
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate("/ebench/jobs")}
            sx={{
              background: GRADIENT,
              "&:hover": { background: GRADIENT_HOVER },
            }}
          >
            Back to Jobs
          </Button>
        </Box>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />

      <Box
        sx={{
          minHeight: "100vh",
          py: 4,
          px: { xs: 2, md: 6 },
          background:
            "linear-gradient(180deg, rgba(45,109,150,0.05), #ffffff 40%)",
        }}
      >
        {/* Back Button */}
        <Button
          startIcon={<FaArrowLeft />}
          onClick={() => navigate("/ebench/jobs")}
          sx={{
            mb: 3,
            color: BRAND,
            fontWeight: 600,
            textTransform: "none",
            "&:hover": {
              backgroundColor: "rgba(45,109,150,0.08)",
            },
          }}
        >
          Back to Jobs
        </Button>

        <Grid container spacing={4}>
          {/* LEFT CONTENT */}
          <Grid item xs={12} md={8}>
            {/* Job Header Card */}
            <Paper
              elevation={0}
              sx={{
                borderRadius: 4,
                mb: 3,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
              }}
            >
              {/* Header with gradient background */}
              <Box
                sx={{
                  background: GRADIENT,
                  p: 4,
                  color: "#fff",
                }}
              >
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="flex-start"
                >
                  <Box display="flex" gap={2} flex={1}>
                    <Avatar
                      sx={{
                        width: 72,
                        height: 72,
                        bgcolor: "rgba(255,255,255,0.2)",
                        color: "#fff",
                        fontSize: "2rem",
                        fontWeight: 700,
                        border: "3px solid rgba(255,255,255,0.3)",
                      }}
                    >
                      {jobDetails?.jobTitle?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h4" fontWeight={800} mb={1}>
                        {jobDetails?.jobTitle}
                      </Typography>
                      {/* <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{ opacity: 0.95 }}
                      >
                        {jobDetails?.companyName}
                      </Typography> */}
                    </Box>
                  </Box>

                  <Box display="flex" gap={1}>
                    <Tooltip title={isSaved ? "Remove from saved" : "Save job"}>
                      <IconButton
                        onClick={() => setIsSaved(!isSaved)}
                        sx={{
                          color: "#fff",
                          bgcolor: "rgba(255,255,255,0.15)",
                          "&:hover": {
                            bgcolor: "rgba(255,255,255,0.25)",
                          },
                        }}
                      >
                        {isSaved ? <FaBookmark /> : <FaRegBookmark />}
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>

                {/* Quick Info */}
                <Box
                  display="flex"
                  gap={3}
                  mt={3}
                  flexWrap="wrap"
                  sx={{ opacity: 0.95 }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    <FaMapMarkerAlt />

                    <Typography>
                      {jobDetails?.workType === "REMOTE"
                        ? "Remote"
                        : ["ONSITE", "HYBRID"].includes(jobDetails?.workType)
                        ? location.join(", ") || "N/A"
                        : safe(jobDetails?.jobLocation)}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <FaBriefcase />
                    <Typography>
                      {formatExperience(
                        jobDetails?.minimumExperience,
                        jobDetails?.maximumExperience
                      )}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <FaClock />
                    <Typography>
                      {jobDetails?.employmentType
                        ? jobDetails.employmentType === "FULL_TIME"
                          ? "Full Time"
                          : jobDetails.employmentType === "PART_TIME"
                          ? "Part Time"
                          : jobDetails.employmentType === "ONSITE"
                          ? "Onsite"
                          : jobDetails.employmentType === "REMOTE"
                          ? "Remote"
                          : jobDetails.employmentType
                        : "N/A"}
                    </Typography>
                  </Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    {jobDetails?.jobType === "CONTRACT" ? (
                      <FaFileSignature />
                    ) : jobDetails?.jobType === "PERMANENT" ? (
                      <FaBriefcase />
                    ) : jobDetails?.jobType === "FREELANCE" ? (
                      <FaLaptopCode />
                    ) : jobDetails?.jobType === "INTERNSHIP" ? (
                      <FaUserGraduate />
                    ) : (
                      <FaQuestionCircle />
                    )}

                    <Typography>
                      {jobDetails?.jobType === "CONTRACT"
                        ? "Contract"
                        : jobDetails?.jobType === "PERMANENT"
                        ? "Permanent"
                        : jobDetails?.jobType === "FREELANCE"
                        ? "Freelance"
                        : jobDetails?.jobType === "INTERNSHIP"
                        ? "Internship"
                        : "N/A"}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Share Section */}
              <Box
                sx={{
                  px: 3,
                  py: 2,
                  mt: 2,
                  borderRadius: 2,
                  background: `
                    linear-gradient(
                      135deg,
                      rgba(45,109,150,0.12) 0%,
                      rgba(45,109,150,0.04) 60%,
                      rgba(45,109,150,0.02) 100%
                    )
                  `,
                  border: "1px solid rgba(45,109,150,0.18)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 600,
                    color: "#2d6d96",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                  }}
                >
                  <FaShareAlt size={14} />
                  Share this job
                </Typography>

                <ShareButtons title={jobDetails?.jobTitle} />
              </Box>
            </Paper>

            {/* Job Content Card */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 4,
                border: "1px solid rgba(0,0,0,0.08)",
              }}
            >
              <CardContent sx={{ p: 4 }}>
                {/* Description */}
                <Box mb={4}>
                  <Typography
                    variant="h5"
                    fontWeight={700}
                    color={BRAND}
                    mb={2}
                    display="flex"
                    alignItems="center"
                    gap={1}
                  >
                    About this role
                  </Typography>
                  <Typography
                    variant="body1"
                    color="text.secondary"
                    lineHeight={1.8}
                    sx={{ textAlign: "justify" }}
                  >
                    <p
                      className="eBench-job-description mb-2"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          jobDetails?.jobDescription || "N/A"
                        ),
                      }}
                    ></p>
                  </Typography>
                </Box>

                <Divider sx={{ my: 4 }} />

                {/* Requirements */}
                {/* {jobDetails?.requiredSkills &&
                  jobDetails.requiredSkills.length > 0 && (
                    <Box mb={4}>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        color={BRAND}
                        mb={2}
                      >
                        Requirements & Skills
                      </Typography>
                      <Stack spacing={1.5}>
                        {jobDetails.requiredSkills.map((item, idx) => (
                          <Box
                            key={idx}
                            display="flex"
                            gap={2}
                            alignItems="flex-start"
                          >
                            <FaCheckCircle
                              style={{
                                color: BRAND,
                                marginTop: "4px",
                                flexShrink: 0,
                              }}
                            />
                            <Typography color="text.secondary" lineHeight={1.7}>
                              {item}
                            </Typography>
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  )} */}

                {/* <Divider sx={{ my: 4 }} /> */}

                {/* Skills Tags */}
                {jobDetails?.requiredSkills &&
                  jobDetails.requiredSkills.length > 0 && (
                    <Box>
                      <Typography
                        variant="h5"
                        fontWeight={700}
                        color={BRAND}
                        mb={2}
                      >
                        Required Skills
                      </Typography>
                      <Box display="flex" gap={1.5} flexWrap="wrap">
                        {jobDetails.requiredSkills.map((skill) => (
                          <Chip
                            key={skill}
                            label={skill}
                            sx={{
                              backgroundColor: "rgba(45,109,150,0.1)",
                              color: BRAND,
                              fontWeight: 600,
                              fontSize: "0.9rem",
                              py: 2.5,
                              px: 1,
                              "&:hover": {
                                backgroundColor: "rgba(45,109,150,0.15)",
                              },
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
              </CardContent>
            </Card>
          </Grid>

          {/* RIGHT SIDEBAR */}
          <Grid item xs={12} md={4}>
            <Box
              sx={{
                position: "sticky",
                top: 100,
              }}
            >
              {/* Apply Card */}
              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  mb: 3,
                  border: "1px solid rgba(0,0,0,0.08)",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    background: "rgba(45,109,150,0.05)",
                    p: 3,
                    borderBottom: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <Typography variant="h6" fontWeight={700} color={BRAND}>
                    Ready to apply?
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    Join our team and make an impact
                  </Typography>
                </Box>

                <CardContent sx={{ p: 3 }}>
                  <Button
                    fullWidth
                    onClick={handleApply}
                    size="large"
                    sx={{
                      py: 1.5,
                      borderRadius: 3,
                      fontWeight: 700,
                      fontSize: "1rem",
                      textTransform: "none",
                      background: GRADIENT,
                      color: "#fff",
                      boxShadow: "0 8px 20px rgba(45,109,150,0.35)",
                      "&:hover": {
                        background: GRADIENT_HOVER,
                        transform: "translateY(-2px)",
                        boxShadow: "0 12px 24px rgba(45,109,150,0.4)",
                      },
                      transition: "all 0.3s ease",
                    }}
                  >
                    Apply Now
                  </Button>
                </CardContent>
              </Card>

              {/* Job Overview Card */}
              <Card
                elevation={0}
                sx={{
                  borderRadius: 4,
                  border: "1px solid rgba(0,0,0,0.08)",
                }}
              >
                <Box
                  sx={{
                    background: "rgba(45,109,150,0.05)",
                    p: 3,
                    borderBottom: "1px solid rgba(0,0,0,0.08)",
                  }}
                >
                  <Typography variant="h6" fontWeight={700} color={BRAND}>
                    Job Overview
                  </Typography>
                </Box>

                <CardContent sx={{ p: 3 }}>
                  <Stack spacing={2.5}>
                    <Box>
                      <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            background: "rgba(45,109,150,0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: BRAND,
                          }}
                        >
                          <FaMapMarkerAlt />
                        </Box>
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                          >
                            Location
                          </Typography>
                          <Typography fontWeight={600}>
                            {jobDetails?.workType === "REMOTE"
                              ? "Remote"
                              : ["ONSITE", "HYBRID"].includes(
                                  jobDetails?.workType
                                )
                              ? location.join(", ") || "N/A"
                              : safe(jobDetails?.jobLocation)}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Divider />
                    <Box>
                      <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            background: "rgba(45,109,150,0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: BRAND,
                          }}
                        >
                          {jobDetails?.jobType === "CONTRACT" ? (
                            <FaFileSignature />
                          ) : jobDetails?.jobType === "PERMANENT" ? (
                            <FaBriefcase />
                          ) : jobDetails?.jobType === "FREELANCE" ? (
                            <FaLaptopCode />
                          ) : jobDetails?.jobType === "INTERNSHIP" ? (
                            <FaUserGraduate />
                          ) : (
                            <FaQuestionCircle />
                          )}
                        </Box>

                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                          >
                            Job Type
                          </Typography>

                          <Typography fontWeight={600}>
                            {jobDetails?.jobType === "CONTRACT"
                              ? "Contract"
                              : jobDetails?.jobType === "PERMANENT"
                              ? "Permanent"
                              : jobDetails?.jobType === "FREELANCE"
                              ? "Freelance"
                              : jobDetails?.jobType === "INTERNSHIP"
                              ? "Internship"
                              : "N/A"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    <Divider />

                    <Box>
                      <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            background: "rgba(45,109,150,0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: BRAND,
                          }}
                        >
                          <FaBriefcase />
                        </Box>
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                          >
                            Experience
                          </Typography>
                          <Typography fontWeight={600}>
                            {formatExperience(
                              jobDetails?.minimumExperience,
                              jobDetails?.maximumExperience
                            )}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>

                    <Divider />

                    <Box>
                      <Box display="flex" alignItems="center" gap={1.5} mb={1}>
                        <Box
                          sx={{
                            width: 40,
                            height: 40,
                            borderRadius: 2,
                            background: "rgba(45,109,150,0.1)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: BRAND,
                          }}
                        >
                          <FaClock />
                        </Box>
                        <Box>
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            fontWeight={600}
                          >
                            Employment Type
                          </Typography>
                          <Typography fontWeight={600}>
                            {jobDetails?.employmentType
                              ? jobDetails.employmentType === "FULL_TIME"
                                ? "Full Time"
                                : jobDetails.employmentType === "PART_TIME"
                                ? "Part Time"
                                : jobDetails.employmentType === "FLEXIBLE"
                                ? "Flexible"
                                : jobDetails.employmentType
                              : "N/A"}
                          </Typography>
                        </Box>
                      </Box>
                    </Box>
                    {jobDetails?.salary && (
                      <>
                        <Divider />
                        <Box>
                          <Box
                            display="flex"
                            alignItems="center"
                            gap={1.5}
                            mb={1}
                          >
                            <Box
                              sx={{
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                background: "rgba(45,109,150,0.1)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: BRAND,
                              }}
                            >
                              <FaMoneyBillWave />
                            </Box>
                            <Box>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={600}
                              >
                                Salary Range
                              </Typography>
                              <Typography fontWeight={600}>
                                {jobDetails?.salary}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      </>
                    )}
                  </Stack>
                </CardContent>
              </Card>
            </Box>
          </Grid>
        </Grid>
      </Box>

      <Footer />
    </>
  );
};

export default JobDetails;
